<script setup lang="ts">
type ErrorLike = boolean | string | null | undefined | Ref<boolean | string | null | undefined>;

type Props = {
  modelValue: boolean
  id?: string
  name?: string
  error?: ErrorLike
  disabled?: boolean
  requiredStrokeColor?: string
  normalStrokeColor?: string
}

const props = withDefaults(defineProps<Props>(), {
  id: undefined,
  name: undefined,
  error: false,
  disabled: false,
  requiredStrokeColor: '#D40606',
  normalStrokeColor: '#4686FF'
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'change', value: boolean): void
}>()

const rawUid = useId();
const safeUid = computed(() => rawUid.replace(/:/g, ''));
const inputId = computed(() => props.id ?? `base_checkbox${safeUid.value}`);
const inputName = computed(() => props.name ?? `base_checkbox${safeUid.value}`);

const strokeColor = computed(() => !props.modelValue && props.error ? props.requiredStrokeColor : props.normalStrokeColor);

const onChange = (e: Event) => {
  const target = e.target as HTMLInputElement
  emit('update:modelValue', target.checked)
  emit('change', target.checked)
}
</script>
<template>
  <div class="wad4-base-checkbox">
    <input
        type="checkbox"
        :id="inputId"
        :name="inputName"
        :checked="modelValue"
        :disabled="disabled"
        @change="onChange"
        :aria-invalid="!!error || undefined"
        :aria-describedby="error ? `${inputId}-error` : undefined"
    />
    <label class="wad4-base-checkbox__label" :for="inputId">
      <svg
          v-show="!modelValue"
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="25"
          viewBox="0 0 24 25"
          fill="none"
      >
        <rect
            x="3.5"
            y="4.33984"
            width="17"
            height="17"
            rx="1.5"
            :stroke="strokeColor"
        />
        <polyline class="checkmark" points="6 12 10 16 18 8"></polyline>
      </svg>
      <svg
          v-show="modelValue"
          width="24"
          height="25"
          viewBox="0 0 24 25"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
      >
        <mask
            :id="`mask_${inputId}`"
            style="mask-type:alpha"
            maskUnits="userSpaceOnUse"
            x="3"
            y="3"
            width="18"
            height="19"
        >
          <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M5 3.83984H19C20.11 3.83984 21 4.73984 21 5.83984V19.8398C21 20.9398 20.11 21.8398 19 21.8398H5C3.89 21.8398 3 20.9398 3 19.8398V5.83984C3 4.73984 3.89 3.83984 5 3.83984ZM5 12.8398L10 17.8398L19 8.83984L17.59 7.41984L10 15.0098L6.41 11.4298L5 12.8398Z"
              fill="white"
          />
        </mask>
        <g :mask="`url(#mask_${inputId})`">
          <rect y="0.839844" width="24" height="24" fill="currentColor" />
        </g>
      </svg>
      <span class="wad4-base-checkbox__content">
        <slot />
      </span>
    </label>
  </div>
  <div v-if="error" :id="`${inputId}-error`" class="wad4-base-checkbox__error">
    {{ typeof error === 'string' ? error : 'This is a required field' }}
  </div>
</template>

<style lang="scss">
.wad4-base-checkbox {
  color: $wad4-c-border-border-brand-solid;

  input[type="checkbox"] {
    display: none;
  }

  &__label {
    position: relative;
    display: flex;
    align-items: flex-start;
    cursor: pointer;
    gap: 4px;
    font-size: 14px;
    line-height: 20px;
    color: $wad4-c-text-text-primary;

    svg {
      flex-shrink: 0;
      color: $wad4-c-border-border-brand-solid;
    }
  }

  @include mqBreakPoint(0, $wad4-breakpoint-mobile-max) {
    &__content {
      letter-spacing: -0.005em;
    }
  }

  & a {
    font-size: 14px !important;
    line-height: 20px !important;
    color: $wad4-c-background-bg-brand-solid !important;
    text-decoration: unset;
  }

  &__error {
    color: $wad4-c-text-text-error;
    font-size: 12px;
  }
}
</style>
